/**
 * We use English comments only in this project.
 */
import { create } from 'zustand';
import { defaultCenter, GOOGLE_MAPS_API_KEY } from './constants';
import { 
  logDebug, 
  extractLocationName, 
  filterNearbyPlaces, 
  filterGeocoderResults,
  searchByTextModern,
  findNearbyPlacesModern
} from './utils';
import { searchBounds } from './constants';

// Helper function to ensure Places API is loaded 
export const ensurePlacesApiLoaded = async (): Promise<boolean> => {
  // Check if Google Maps API is loaded
  if (!window.google || !window.google.maps) {
    console.error('Google Maps API is not available');
    return false;
  }
  
  try {
    // Try to load the Places library
    const { Place } = await google.maps.importLibrary("places") as { Place: any };
    console.log('Modern Places API loaded successfully');
    return true;
  } catch (error) {
    console.error('Failed to load modern Places API:', error);
    return false;
  }
};

// Types
export type Location = {
  lat: number;
  lng: number;
};

export type AddressData = {
  address: string;
  locationName: string;
  alternateAddresses: {name: string, address: string}[];
};

// Current location state type
interface CurrentLocationData {
  coords: Location | null;
  address: string;
  loading: boolean;
  error: any;
}

// Helper to get preferred language based on browser settings
const getPreferredLanguage = (): string => {
  // Get browser language
  const browserLang = navigator.language || (navigator as any).userLanguage || '';
  
  // Check if Thai is preferred
  if (browserLang.toLowerCase().includes('th')) {
    return 'th';
  }
  
  // Default to English
  return 'en';
};

// Map Location Store
interface MapLocationState {
  // Map position and location data
  markerPosition: Location;
  address: string;
  locationName: string;
  alternateAddresses: {name: string, address: string}[];
  lastPosition: Location;
  isWithinServiceArea: boolean;
  
  // Additional address details from place selection
  addressDetails?: {
    formatted_address?: string;
    name?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    latitude?: number;
    longitude?: number;
  };
  
  // UI states
  searchQuery: string;
  isSearching: boolean;
  isGeocoding: boolean;
  isLoading: boolean;
  isAddressEditing: boolean;
  
  // Flag to prevent address jumping/overriding after selection
  isAddressLocked: boolean;
  
  // Current location state
  currentLocation: CurrentLocationData;
  
  // Current step of the location modal (1 = location selection, 2 = address details)
  currentStep: 1 | 2;
  
  // Flag to indicate a programmatic map update is in progress
  isProgrammaticUpdate: boolean;
  
  // User location
  userLocation: Location | null;
  
  // Map reference (not actually stored in Zustand, but passed through)
  map: google.maps.Map | null;
  
  // Actions
  setMarkerPosition: (position: Location) => void;
  setAddress: (address: string) => void;
  setLocationName: (name: string) => void;
  setAlternateAddresses: (addresses: {name: string, address: string}[]) => void;
  updatePosition: (position: Location) => void;
  setSearchQuery: (query: string) => void;
  setIsSearching: (isSearching: boolean) => void;
  setIsWithinServiceArea: (isWithin: boolean) => void;
  setCurrentStep: (step: 1 | 2) => void;
  setMap: (map: google.maps.Map | null) => void;
  setProgrammaticUpdate: (isUpdating: boolean) => void;
  setIsAddressEditing: (isEditing: boolean) => void;
  setIsAddressLocked: (isLocked: boolean) => void;
  
  // Current location actions
  setCurrentLocation: (locationData: Partial<CurrentLocationData>) => void;
  
  // Geocoding functions
  geocodePosition: (position: Location) => Promise<AddressData>;
  searchAddress: (query: string) => Promise<{position: Location, address: string, locationName: string}>;
  
  // Nearby places function
  findNearbyPlaces: (
    map: google.maps.Map, 
    position: Location,
    currentAddress?: string
  ) => Promise<{name: string, address: string}[]>;
  
  // User location functions
  getUserLocation: () => Promise<Location>;
  getCurrentLocation: () => Promise<Location | null>;
  handleCurrentLocationSelect: () => Promise<Location | null>;
  loadGoogleMapsAPI: () => Promise<void>;
  
  // Modern API functions
  searchAddressModern: (query: string) => Promise<{position: Location, address: string, locationName: string}>;
  findNearbyPlacesModern: (
    map: google.maps.Map, 
    position: Location,
    currentAddress?: string
  ) => Promise<{name: string, address: string}[]>;
  
  // Internal flags to control map behavior
  isFromSearch: boolean; // New flag to track when position comes from search
  
  // Initialization function
  initializeLocation: () => void;
}

// Create the store with initial state
export const useMapStore = create<MapLocationState>((set, get) => ({
  // Initial map position and location data
  markerPosition: defaultCenter,
  address: '',
  locationName: '',
  alternateAddresses: [],
  lastPosition: defaultCenter,
  isWithinServiceArea: true,
  addressDetails: undefined,
  
  // UI states
  searchQuery: '',
  isSearching: false,
  isGeocoding: false,
  isLoading: false,
  isAddressEditing: false,
  
  // Add isAddressLocked with default false value
  isAddressLocked: false,
  
  // Current location
  currentLocation: {
    coords: null,
    address: '',
    loading: false,
    error: null,
  },
  
  // Internal flags
  isProgrammaticUpdate: false,
  isFromSearch: false, // Initialize as false
  
  // References
  map: null,
  
  // Current step of the location modal (1 = location selection, 2 = address details)
  currentStep: 1, // Default to location selection step
  
  // User location (using defaultCenter instead of null to match Location type)
  userLocation: null,
  
  // Actions
  setMarkerPosition: (position) => set({ markerPosition: position }),
  setAddress: (address) => set({ address }),
  setLocationName: (locationName) => set({ locationName }),
  setAlternateAddresses: (alternateAddresses) => set((state) => {
    // Use reference comparison to avoid unnecessary state updates
    if (state.alternateAddresses.length === alternateAddresses.length && 
        JSON.stringify(state.alternateAddresses) === JSON.stringify(alternateAddresses)) {
      console.log("[DEBUG] Skipping alternateAddresses update - identical data");
      return {}; // No state update needed
    }
    return { alternateAddresses };
  }),
  updatePosition: (position) => {
    // Check if this is a null or invalid position
    if (!position || typeof position.lat !== 'number' || typeof position.lng !== 'number' || 
        isNaN(position.lat) || isNaN(position.lng)) {
      console.error("[MapStore] Attempted to update with invalid position:", position);
      return;
    }
    
    console.log("[MapStore] Updating position:", position);
    set({ 
      markerPosition: position,
      lastPosition: position,
      // Mark that this was a direct position update
      isProgrammaticUpdate: true
    });
  },
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setIsSearching: (isSearching) => set({ isSearching }),
  setIsWithinServiceArea: (isWithinServiceArea) => set({ isWithinServiceArea }),
  setCurrentStep: (currentStep) => {
    console.log(`[MapStore] Changing step to: ${currentStep}`);
    set({ currentStep });
  },
  setMap: (map) => set({ map }),
  setProgrammaticUpdate: (isProgrammaticUpdate) => {
    console.log(`[MapStore] Setting isProgrammaticUpdate: ${isProgrammaticUpdate}`);
    set({ isProgrammaticUpdate });
  },
  setIsAddressEditing: (isAddressEditing) => set({ isAddressEditing }),
  
  // Set isAddressLocked function
  setIsAddressLocked: (isAddressLocked) => set({ isAddressLocked }),
  
  // Current location actions
  setCurrentLocation: (locationData) => set((state) => ({
    currentLocation: { ...state.currentLocation, ...locationData }
  })),
  
  // Helper function to load Google Maps API
  loadGoogleMapsAPI: async () => {
    // If API is already loaded, resolve immediately
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }
    
    // Use the Places API loader since it's more comprehensive
    try {
      await ensurePlacesApiLoaded();
      return;
    } catch (error) {
      console.error('Failed to load Google Maps API:', error);
      throw new Error('Failed to load Google Maps API');
    }
  },
  
  // Geocoding
  geocodePosition: async (position) => {
    set({ isGeocoding: true });
    
    try {
      if (!window.google || !window.google.maps) {
        throw new Error('Google Maps API not loaded');
      }
      
      return new Promise<AddressData>((resolve, reject) => {
        const geocoder = new google.maps.Geocoder();
        
        geocoder.geocode({ 
          location: position,
          language: getPreferredLanguage()
        }, (results, status) => {
          set({ isGeocoding: false });
          
          if (status === "OK" && results && results[0]) {
            const address = results[0].formatted_address;
            const locationName = extractLocationName(results[0]);
            
            // Default empty alternates, to be filled by the caller if needed
            const result: AddressData = {
              address,
              locationName,
              alternateAddresses: []
            };
            
            if (results.length > 1) {
              result.alternateAddresses = filterGeocoderResults(results);
            }
            
            resolve(result);
          } else {
            reject({
              status,
              message: 'Geocoding failed'
            });
          }
        });
      });
    } catch (error) {
      set({ isGeocoding: false });
      throw error;
    }
  },
  
  searchAddress: async (query) => {
    set({ isGeocoding: true });
    
    try {
      if (!window.google || !window.google.maps) {
        throw new Error('Google Maps API not loaded');
      }
      
      return new Promise<{position: Location, address: string, locationName: string}>((resolve, reject) => {
        const geocoder = new google.maps.Geocoder();
        
        geocoder.geocode({ 
          address: query,
          bounds: new google.maps.LatLngBounds(
            new google.maps.LatLng(searchBounds.south, searchBounds.west),
            new google.maps.LatLng(searchBounds.north, searchBounds.east)
          ),
          componentRestrictions: { country: 'th' },
          language: getPreferredLanguage()
        }, (results, status) => {
          set({ isGeocoding: false });
          
          if (status === "OK" && results && results[0]) {
            const place = results[0];
            if (place.geometry && place.geometry.location) {
              const position = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              };
              
              const address = place.formatted_address || query;
              let locationName = '';
              
              if (place.address_components) {
                const poiComponent = place.address_components.find(component => 
                  component.types.includes('establishment') || 
                  component.types.includes('point_of_interest')
                );
                
                if (poiComponent) {
                  locationName = poiComponent.long_name;
                } else {
                  const premiseComponent = place.address_components.find(component => 
                    component.types.includes('premise') || 
                    component.types.includes('subpremise')
                  );
                  
                  if (premiseComponent) {
                    locationName = premiseComponent.long_name;
                  } else {
                    locationName = place.formatted_address?.split(',')[0] || query;
                  }
                }
              } else {
                locationName = query;
              }
              
              resolve({ position, address, locationName });
            } else {
              reject({
                status: 'GEOMETRY_ERROR',
                message: 'No geometry in result'
              });
            }
          } else {
            reject({
              status,
              message: 'Search geocoding failed'
            });
          }
        });
      });
    } catch (error) {
      set({ isGeocoding: false });
      throw error;
    }
  },
  
  // Nearby places
  findNearbyPlaces: async (map, position, currentAddress) => {
    set({ isLoading: true });
    
    try {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        throw new Error('Google Maps Places API not loaded');
      }
      
      return new Promise<{name: string, address: string}[]>((resolve, reject) => {
        const placesService = new google.maps.places.PlacesService(map);
        
        placesService.nearbySearch({
          location: position,
          type: 'establishment',
          rankBy: google.maps.places.RankBy.DISTANCE
        }, (places, status) => {
          set({ isLoading: false });
          
          if (status === google.maps.places.PlacesServiceStatus.OK && places) {
            const filteredPlaces = filterNearbyPlaces(places, currentAddress);
            resolve(filteredPlaces);
          } else {
            resolve([]);
          }
        });
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  // User location
  getUserLocation: async () => {
    set({ isLoading: true });
    
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported by this browser.');
      }
      
      return new Promise<Location>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          position => {
            const pos = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            
            logDebug('Current location detected', pos);
            set({ 
              userLocation: pos,
              isLoading: false 
            });
            resolve(pos);
          },
          error => {
            logDebug('Error getting current location', error);
            set({ isLoading: false });
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      });
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },
  
  // Modern API implementations
  searchAddressModern: async (query) => {
    set({ isGeocoding: true });
    
    try {
      // Use the modern search API
      const result = await searchByTextModern(query);
      set({ isGeocoding: false });
      
      return {
        position: result.position,
        address: result.address,
        locationName: result.locationName
      };
    } catch (error) {
      set({ isGeocoding: false });
      throw error;
    }
  },
  
  findNearbyPlacesModern: async (map, position, currentAddress) => {
    set({ isLoading: true });
    
    try {
      // Use the modern nearby places API
      const places = await findNearbyPlacesModern(map, position, currentAddress);
      set({ isLoading: false });
      return places;
    } catch (error) {
      set({ isLoading: false });
      console.error('Error finding nearby places with modern API:', error);
      return [];
    }
  },
  
  // Current location functions
  getCurrentLocation: async () => {
    const state = get();
    
    if (!navigator.geolocation) {
      set({ 
        currentLocation: { 
          ...state.currentLocation,
          error: 'Geolocation is not supported by your browser',
          loading: false
        } 
      });
      return null;
    }
    
    set({ 
      currentLocation: { 
        ...state.currentLocation,
        loading: true
      } 
    });
    
    try {
      // First ensure Google Maps API is loaded
      await ensurePlacesApiLoaded();
      
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      // If we got coordinates, also update userLocation
      set({ userLocation: coords });
      
      // Check if window.google is available (API loaded)
      if (!window.google || !window.google.maps) {
        throw new Error('Google Maps API not loaded');
      }
      
      const geocoder = new window.google.maps.Geocoder();
      
      try {
        // Use Promise to handle geocoder callback
        const results = await new Promise<any[]>((resolve, reject) => {
          geocoder.geocode({ location: coords }, (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
            if (status === "OK") {
              resolve(results as any[]);
            } else {
              reject(new Error(`Geocoding failed: ${status}`));
            }
          });
        });
        
        if (results && results[0]) {
          set({ 
            currentLocation: {
              address: results[0].formatted_address,
              coords,
              loading: false,
              error: undefined
            }
          });
          
          return coords;
        } else {
          throw new Error('No results from geocoder');
        }
      } catch (geocodeError) {
        console.error('Geocoding error:', geocodeError);
        
        // Even if geocoding fails, return the coordinates
        set({ 
          currentLocation: {
            address: 'Location found, but address details unavailable',
            coords,
            loading: false,
            error: 'Could not get address details for this location'
          }
        });
        
        return coords;
      }
    } catch (err) {
      console.error('Error getting current location:', err);
      let errorMessage = 'Unable to retrieve your location';
      
      // Generic error handling
      set({ 
        currentLocation: {
          coords: null,
          address: 'Location access denied',
          loading: false,
          error: errorMessage
        }
      });
      
      return null;
    }
  },
  
  handleCurrentLocationSelect: async () => {
    return await get().getCurrentLocation();
  },

  // Initialize with saved location - this function runs when store is created
  initializeLocation: () => {
    // Get saved location from localStorage if available
    const savedLocationString = localStorage.getItem('piika_location');
    
    // If a saved location exists in localStorage, update state with it
    if (savedLocationString) {
      try {
        const savedLocation = JSON.parse(savedLocationString);
        
        if (savedLocation && savedLocation.lat && savedLocation.lng) {
          // Ensure it's actually a valid location
          const validLat = typeof savedLocation.lat === 'number' && !isNaN(savedLocation.lat);
          const validLng = typeof savedLocation.lng === 'number' && !isNaN(savedLocation.lng);
          
          if (validLat && validLng) {
            // Default to using saved location
            set({
              markerPosition: savedLocation,
              lastPosition: savedLocation
            });
            
            // If no address in memory, try to retrieve it
            const savedAddressString = localStorage.getItem('piika_cache_address');
            if (savedAddressString) {
              try {
                const savedAddress = JSON.parse(savedAddressString);
                if (savedAddress && savedAddress.address) {
                  set({
                    address: savedAddress.address
                  });
                }
              } catch (error) {
                console.error('Error parsing saved address:', error);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error parsing saved location:', error);
      }
    }
    // Removed automatic geolocation request - location should only be requested
    // when user explicitly needs it (e.g., clicking "Use Current Location")
  }
}));

// Remove automatic initialization to prevent location permission requests on page load
// useMapStore.getState().initializeLocation(); 